'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Search } from 'lucide-react';
import { useGetAllStaff, useDeleteStaff } from '../../api/staff/staffQueries';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog.tsx';
import { StaffEditPanel } from './StaffEditPanel';
import { InlineFilter } from '../ui/inline-filter.tsx';
import { motion } from 'framer-motion';
import StaffTeamTableCard from './StaffTeamTableCard.jsx';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table.tsx';

export function TeamTable({ title, subtitle }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [processedMembers, setProcessedMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: {
      active: false,
      inactive: false,
    },
    department: {
      assigned: false,
      unassigned: false,
    },
    role: {
      staff: false,
      admin: false,
      teacher: false,
    },
  });
  const navigate = useNavigate();

  const filterGroups = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { id: 'active', label: 'Active', checked: filters.status.active },
        { id: 'inactive', label: 'Inactive', checked: filters.status.inactive },
      ],
    },
    {
      id: 'department',
      label: 'Department',
      options: [
        {
          id: 'assigned',
          label: 'Assigned',
          checked: filters.department.assigned,
        },
        {
          id: 'unassigned',
          label: 'Unassigned',
          checked: filters.department.unassigned,
        },
      ],
    },
    {
      id: 'role',
      label: 'Role',
      options: [
        { id: 'staff', label: 'Staff', checked: filters.role.staff },
        { id: 'admin', label: 'Admin', checked: filters.role.admin },
        { id: 'teacher', label: 'Teacher', checked: filters.role.teacher },
      ],
    },
  ];

  const handleFilterChange = (groupId, optionId, checked) => {
    setFilters((prev) => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [optionId]: checked,
      },
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      status: {
        active: false,
        inactive: false,
      },
      department: {
        assigned: false,
        unassigned: false,
      },
      role: {
        staff: false,
        admin: false,
        teacher: false,
      },
    });
  };

  // Use getAllStaff query hook to fetch all team members
  const { data: staffData, isLoading, error } = useGetAllStaff();
  const { mutate: deleteStaff, isPending: isDeleting } = useDeleteStaff();

  // Process and normalize the staff data
  useEffect(() => {
    if (!staffData) {
      setProcessedMembers([]);
      return;
    }
    setProcessedMembers(staffData);
  }, [staffData]);

  const handleDelete = (member, e) => {
    e.stopPropagation();
    setSelectedMember(member);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    // Get the staffId which is the primary identifier used by the backend
    const staffIdToDelete = selectedMember?.staffId || selectedMember?.id;

    if (!staffIdToDelete) {
      toast.error('Unable to delete staff member: ID not found');
      return;
    }

    deleteStaff(staffIdToDelete, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        setSelectedMember(null);
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || 'Failed to delete staff member'
        );
        setShowDeleteDialog(false);
        setSelectedMember(null);
      },
    });
  };

  const handleEdit = (member, e) => {
    e.stopPropagation();
    // Normalize member data based on the actual query response structure
    const normalizedMember = {
      id: member.id || member.staffId,
      staffId: member.staffId || member.id,
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      name:
        member.name ||
        `${member.firstName || ''} ${member.lastName || ''}`.trim(),
      email: member.email || '',
      phone: member.phone || member.phoneNumber || '',
      phoneNumber: member.phoneNumber || member.phone || '',
      role: member.role || 'staff',
      department: member.department || '',
      isActive: member.isActive || member.status === 'active',
      status: member.status || (member.isActive ? 'active' : 'inactive'),
      joinDate: member.joinDate || member.createdAt || new Date().toISOString(),
      photoUrl: member.photoUrl || member.avatar || '/placeholder.svg',
      avatar: member.avatar || member.photoUrl || '/placeholder.svg',
      designation: member.designation || '',
      departmentId: member.departmentId || '',
      roleId: member.roleId || '',
      dob: member.dob || null,
      bloodGroup: member.bloodGroup || '',
      address: member.address || '',
      salary: member.salary || '',
    };
    setSelectedMember(normalizedMember);
    setIsEditPanelOpen(true);
  };

  // Todo: Make this filter work again
  // Filter the processed members based on search query and filters
  const filteredMembers = processedMembers.filter((member) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
      const department =
        member.department?.name?.toLowerCase() ||
        member.department?.toLowerCase() ||
        '';

      const matchesSearch =
        fullName.includes(query) ||
        department.includes(query) ||
        member.email?.toLowerCase().includes(query) ||
        member.phone?.toString().includes(query) ||
        member.designation?.toLowerCase().includes(query) ||
        member.role?.toLowerCase().includes(query) ||
        member.baseRole?.toLowerCase().includes(query);

      if (!matchesSearch) return false;
    }

    // Status filter
    if (filters.status.active || filters.status.inactive) {
      const isActive = member.status === 'Active' || member.isActive;
      if (filters.status.active && !isActive) return false;
      if (filters.status.inactive && isActive) return false;
    }

    // Department filter
    if (filters.department.assigned || filters.department.unassigned) {
      const hasDepartment =
        member.department && member.department !== 'Not assigned';
      if (filters.department.assigned && !hasDepartment) return false;
      if (filters.department.unassigned && hasDepartment) return false;
    }

    // Role filter
    if (filters.role.staff || filters.role.admin || filters.role.teacher) {
      const role = (member.Role || member.baseRole || 'Staff').toLowerCase();
      if (filters.role.staff && !role.includes('staff')) return false;
      if (filters.role.admin && !role.includes('admin')) return false;
      if (filters.role.teacher && !role.includes('teacher')) return false;
    }

    return true;
  });

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <motion.div
            className="flex items-center gap-4 flex-1 max-w-[800px]"
            layout
          >
            <div className="flex-1 flex items-center gap-4 min-w-0">
              <InlineFilter
                groups={filterGroups}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
              />
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email or department"
                  className="w-full pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </motion.div>
          <Button
            className="bg-[#7C3AED] hover:bg-[#7C3AED]/90"
            onClick={() => navigate('/team/add')}
          >
            + Add Employee
          </Button>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {isLoading ? (
            <div className="p-8 text-center">Loading staff data...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">
              Error loading staff: {error.message || 'Failed to load data'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/6">Name</TableHead>
                    <TableHead className="w-1/6">Department</TableHead>
                    <TableHead className="w-1/6">Designation</TableHead>
                    <TableHead className="w-1/6">Started</TableHead>
                    <TableHead className="w-1/6">Role</TableHead>
                    <TableHead className="w-1/6">Status</TableHead>
                    <TableHead className="w-1/6">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffData.map((staff) => (
                    <StaffTeamTableCard
                      staff={staff}
                      handleDelete={handleDelete}
                      handleEdit={handleEdit}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <StaffEditPanel
        staff={selectedMember}
        isOpen={isEditPanelOpen}
        onClose={() => {
          setIsEditPanelOpen(false);
          setSelectedMember(null);
        }}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedMember?.firstName}{' '}
              {selectedMember?.lastName}'s profile and remove all associated
              data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Staff Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
